<template>
    <div class="hts-form-radio">
        <span class="label">
            <span
                v-if="label"
                class="label-text">
                {{ label }}
                <span
                    v-if="required"
                    class="text-error-500 ml-1">
                    *
                </span>
            </span>
        </span>
        <div
            class="flex flex-wrap gap-1"
            :class="{ 'flex-col': type === 'list' }">
            <div
                v-for="(item, index) in items"
                :key="index"
                class="pr-2 inline-flex items-center">
                <input
                    :id="name"
                    v-model="check"
                    type="radio"
                    :name="'radio-' + uid"
                    class="radio"
                    :class="cColor"
                    :value="item[valueKey]"
                    :required="required"
                    :disabled="locked"
                    data-type="radio-box" >
                <span
                    v-if="!Array.isArray(getLabel(item[labelKey]))"
                    class="px-2 cursor-pointer"
                    @click="handleClickLabel(item, $event)">
                    {{ getLabel(item[labelKey]) }}
                </span>
                <div v-else>
                    <p
                        v-for="text in getLabel(item[labelKey])"
                        :key="text"
                        class="px-2 cursor-pointer w-full"
                        @click="handleClickLabel(item, $event)">
                        {{ text }}
                    </p>
                </div>
            </div>
        </div>
        <div
            v-if="errorMessage"
            class="hts-form-radio__error">
            {{ errorMessage }}
        </div>
    </div>
</template>

<script setup lang="ts">
import { useField, useFieldError } from 'vee-validate'
import { getCurrentInstance } from 'vue'
import { daisyuiColor } from '~/components/utils/daisyui'

const uid = getCurrentInstance()?.uid.toString()

const props = defineProps({
  modelValue: [String, Number, Boolean],
  items: {
    type: Array as PropType<any[]>,
    default: () => []
  },
  type: String,
  label: String,
  prop: String,
  name: String,
  rules: Object,
  valueKey: {
    type: String,
    default: 'id'
  },
  labelKey: {
    type: String,
    default: 'label'
  },
  flexWrap: Boolean,
  size: String,
  color: String,
  required: Boolean,
  disabled: Boolean,
  readonly: Boolean
})
const { modelValue, prop, name, rules, items, valueKey, labelKey, color, disabled, readonly } = toRefs(props)

const locked = computed(() => disabled.value || readonly.value)

const cColor = daisyuiColor('radio', color?.value)
let field: any
let errorMessage: undefined | ComputedRef
const cProp = computed(() => prop?.value)
const setFields = (prop: any) => {
  if (typeof prop === 'string' && prop !== '') {
    field = useField(prop, undefined, {
      label: name?.value || prop,
      validateOnValueUpdate: true,
      validateOnMount: false
    })
    errorMessage = useFieldError(cProp.value)
  }
}
watch(
  () => cProp.value,
  (val: any) => {
    setFields(val)
  }
)
onMounted(() => {
  setFields(cProp.value)
})

const emits = defineEmits(['update:modelValue', 'change', 'click'])
const check = computed({
  get () {
    return modelValue?.value
  },
  set (val) {
    field?.setValue(val)
    emits('update:modelValue', val)
  }
})
watch(
  () => check.value,
  (val: any) => {
    emits('change', val)
    field?.setValue(val)
  }
)

const handleClickLabel = (item: any, e: any) => {
  if (locked.value) {
    return
  }
  if (e instanceof Event) {
    e.preventDefault()
    e.stopPropagation()
  }
  check.value = item[valueKey.value]
}

onMounted(() => {
  if (modelValue !== undefined && modelValue.value !== null && modelValue.value !== '') {
    field?.setValue(modelValue.value)
  }
  if (modelValue?.value === undefined) {
    emits('update:modelValue', '')
  }
})

const getLabel = (texts: string | string[]): string | string[] => {
  if (!Array.isArray(texts)) {
    return texts
  }

  if (texts.length <= 2) {
    return texts.join(' và ')
  }
  return texts
}
</script>
